<template>
  <div class="bank-id-app">
    <p v-if="preamble">
      {{ preamble }}
    </p>
    <div v-if="bankIdSignLoading" class="bank-id-app__loading-wrapper">
      <Spinner />
      <p>{{ translations?.waitingForBankId }}</p>
    </div>
    <div v-if="qrCodeSrc" class="bank-id-app__qr-code">
      <img
        :src="qrCodeSrc"
        alt="BankID QR"
        width="50"
        height="50" />
    </div>
    <p v-if="errorMessage" class="form-error bank-id-app__error">
      {{ errorMessage }}
    </p>
    <Spinner v-if="translationsLoading" />
    <div v-else class="bank-id-app__buttons" :class="`bank-id-app__buttons--${state}`">
      <button
        v-for="button in buttons"
        :key="button.text"
        class="button"
        :class="button.modifier"
        @click="button.action()">
        {{ button.text }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, onBeforeMount, Ref, ref} from 'vue';
import {useApi} from '@shared/services/api-service';
import {BankIdDevice} from "@features/BankIdApp/Scripts/constants/BankIdDeviceEnum";
import {BankIdAppState, BankIdState} from "@features/BankIdApp/Scripts/types/BankIdAppState";
import {useLoginBlockService} from "@features/BankIdApp/Scripts/services/login-block-service";
import {useBankIdService} from "@features/BankIdApp/Scripts/services/bank-id-service";
import Spinner from "@features/Scripts/shared/components/spinner/Spinner.vue";

const translations: Ref<BankIdAppTranslations | null> = ref(null);
const {get: getTranslations, data: translationsData, loading: translationsLoading} = useApi('bankidapp/gettranslations');
const qrCodeSrc = ref<string | null>(null);
const errorMessage = ref<string | null>(null); // possibly add an error state and return correct buttons for that
let pollHandle: number | null = null;
const emit = defineEmits<{
  (e: 'signed'): void
  (e: 'failed', reason?: string): void
}>();

const bankIdSignLoading = ref(false);

const sessionId = ref<string | null>(null);
const {sign} = defineProps({
  sign: {
    type: Boolean,
    default: false,
  },
});

onBeforeMount(() => {
  getTranslations().then(() => {
    translations.value = translationsData.value;
    if (translations.value) {
      translations.value.initialPreamble = useLoginBlockService().getTranslations()?.preamble;
    }
  });
});

const selectedDevice = ref<BankIdDevice | null>(null);
const state = computed<BankIdAppState>(() =>
    selectedDevice.value === null
        ? BankIdState.Initial
        : selectedDevice.value === BankIdDevice.ThisDevice
            ? BankIdState.ThisDevice
            : BankIdState.ExternalDevice);

const preamble = computed((): string | null => {
  if (bankIdSignLoading.value) {
    return null;
  }

  switch (state.value) {
    case BankIdState.Initial:
      return translations.value?.initialPreamble ?? null;
    case BankIdState.ThisDevice:
      return translations.value?.startBankIdHelp ?? null;
    case BankIdState.ExternalDevice:
      return translations.value?.startBankIdExternalHelp ?? null;
    default:
      return null;
  }
});

const buttons = computed<BankIdAppButton[]>(() => {
  switch (state.value) {
    case BankIdState.Initial:
      return [
        {
          text: translations.value?.thisDevice,
          action: onThisDevice,
        },
        {
          text: translations.value?.externalDevice,
          action: onOtherDevice,
        },
      ];
    case BankIdState.ThisDevice:
      return [
        {
          text: translations.value?.startBankId,
          action: tryOpenOnThisDevice,
        },
        {
          text: translations.value?.cancel,
          action: cancel,
          modifier: 'button--secondary'
        },
      ];
    case BankIdState.ExternalDevice:
      return [
        {
          text: translations.value?.cancel,
          action: cancel,
        },
      ];
    default:
      return [];
  }
});

const clearPolling = () => {
  if (pollHandle) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
};

const onThisDevice = () => {
  selectedDevice.value = BankIdDevice.ThisDevice;
  startBankId();
};

const onOtherDevice = () => {
  selectedDevice.value = BankIdDevice.ExternalDevice;
  startBankId();
};

const tryOpenOnThisDevice = () => {
  onThisDevice();
};

const cancel = () => {
  useBankIdService().cancelBankId(sessionId.value).then(() => {
    selectedDevice.value = null;
    qrCodeSrc.value = null;
    errorMessage.value = null;
    clearPolling();

    if (sign) {
      emit("failed", "Canceled");
    }
  });
};

const startBankId = () => {
  useBankIdService().startBankIdAuth(selectedDevice.value, isMobileDevice(), sign).then((bankIdResponse) => {

    if (!bankIdResponse) {
      errorMessage.value = "Något gick fel";
      return;
    }

    if (bankIdResponse?.sessionId) {
      sessionId.value = bankIdResponse?.sessionId;
    }

    // qr code
    if (state.value === BankIdState.ExternalDevice) {
      qrCodeSrc.value = bankIdResponse?.qrCode ?? null;
    }

    // Set autostart app
    if (selectedDevice.value == BankIdDevice.ThisDevice && bankIdResponse?.autoStartUrl) {
      window.location.href = bankIdResponse?.autoStartUrl;
    }

    startPolling();
  }).catch((e: unknown) => {
    handleError(e);
  });
};

const startPolling = () => {
  pollHandle = window.setInterval(async () => {
    useBankIdService().statusBankId(sessionId.value ?? '').then((bankIdResponse) => {

      // qr code
      if (state.value === BankIdState.ExternalDevice) {
        qrCodeSrc.value = bankIdResponse?.qrCode ?? null;
      }

      if (bankIdResponse?.status === 'pending') {
        return;
      }

      if (bankIdResponse?.status === 'complete') {
        bankIdSignLoading.value = true;
        clearPolling();

        if (sign) {
          emit('signed');
          return;
        }

        if (getRedirectUrl()) {
          window.location.href = getRedirectUrl() ?? '/';
        }

        return;
      }

      if (bankIdResponse?.status === 'failed' || bankIdResponse?.status === 'cancelled') {
        clearPolling();

        if (sign) {
          emit('failed', bankIdResponse?.hintCode);
        }

        errorMessage.value = bankIdResponse?.hintCode ?? 'Något gick fel';
        bankIdSignLoading.value = false;
      }
    })
    .catch((e: unknown) => {
      handleError(e);
      bankIdSignLoading.value = false;
    });
  }, 5000);
};

const handleError = (e: unknown) => {
  clearPolling();
  errorMessage.value = "Något gick fel";
  console.error(e);
};

const isMobileDevice = () => {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const getRedirectUrl = (): string | null => {
  // Parameter has first dibs
  const params = new URLSearchParams(window.location.search);
    let redirectUrl = params.get('ReturnUrl');

  if (!redirectUrl) {
    // No parameter? Okay, is there data on the app div?
    const app = document.getElementById('bank-id-app');
      redirectUrl = app?.dataset.redirectUrl ?? null;
  }

  return redirectUrl;
};
</script>